// --- React
import React from 'react';
// --- UI Components
import { DetailsModal } from '../../DetailsModal';
import { CreateCommentForm } from './create-form';
import Button from '@dtci/spellbook/Button';
// --- Redux
import { useSelector } from 'react-redux';
import { useCommentsActions } from '../../../store/comments/useComments';
// --- API
import { datastageApi } from '../../../api';
// --- Utilities
import { useLoading } from '../../../utils/hooks/useLoading';
import { filterCriteria } from '../../../utils/filterCriteria';
import { useToastNotification } from '../../ToastNotifications';

/**
 * CreateCommentModal Component
 *
 * This component is responsible for rendering the modal used to create comments.
 * It includes a form for entering comment details and provides options to save or cancel.
 * It also handles the logic for saving the comment and updating the comment list.
 *
 * @component
 * @returns {JSX.Element} The rendered CreateCommentModal component.
 */
export function CreateCommentModal() {
    const { isCreateModalOpen, comment, commentProduct, filterForm, commentRange, commentSort } = useSelector(
        (s) => s.comments,
    );
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { openToast } = useToastNotification();
    const { updateComments } = useCommentsActions();

    // Function to handle the close action of the create comment modal
    const onCloseCreateComment = () => {
        updateComments({
            isCreateModalOpen: false,
        });
    };

    // Function to handle the save action of the comment
    const handleSaveComment = async () => {
        setLoadingTrue();
        try {
            if (!comment) return;

            const res = await datastageApi.saveComment(comment, commentProduct);


            if (res?.data) {
                const commentList = await datastageApi.comments(
                    filterCriteria(filterForm, commentProduct, 'comments'),
                    commentRange,
                    commentSort,
                );

                updateComments({
                    isCreateModalOpen: false,
                    isEditModalOpen: true,
                    comment: res?.data,
                    comments: commentList?.content,
                    totalPages: commentList?.totalPages,
                    totalItems: commentList?.totalElements,
                    currentPage: commentList?.pageable.pageNumber,
                });

                openToast('Comment saved successfully', 'success');
            } else {
                openToast('Error saving comment', 'error');
            }
        } catch (error) {
            console.error('Error saving comment:', error);
            openToast('Error saving comment', 'error');
        } finally {
            setLoadingFalse();
        }
    };

    return (
        <DetailsModal
            isOpen={isCreateModalOpen}
            onClose={onCloseCreateComment}
            modalHeader={'Create Comment'}
            footer={
                <div>
                    <Button
                        iconType={'fa-regular fa-close'}
                        sizeType="small"
                        styleType="link"
                        onClick={onCloseCreateComment}
                    >
                        Cancel
                    </Button>
                    <Button
                        iconType={'fa-regular fa-floppy-disk'}
                        sizeType="small"
                        styleType="link"
                        onClick={handleSaveComment}
                    >
                        Save
                    </Button>
                </div>
            }
        >
            <CreateCommentForm />
        </DetailsModal>
    );
}
